/**
 * PageSelectorSidebar Component (Story 5.33, Tasks 1 + 3; Story 5.36 visual upgrade; Story 5.37 category groups)
 *
 * Left sidebar panel for the report editor showing all pages grouped by category.
 * Supports: add page with category picker, category groups with collapsible sections,
 * page list with active highlighting, drag-and-drop page reorder (separate DndContext from widget DnD),
 * cross-category drag-and-drop, "Move to..." context menu.
 *
 * Story 5.37: Pages are grouped under collapsible category headers. Categories are
 * defined as constants in @provenleap/types/report-rendering. Only categories with
 * at least one page are shown.
 */

import { useState, useMemo, useCallback, useEffect } from "react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { motion } from "motion/react";
import { Plus, CaretDown, SquaresFour, ArrowLineLeft, ArrowLineRight } from "@phosphor-icons/react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@provenleap/ui";
import { Favicon } from "../icons/Favicon";
import { Logo } from "../icons/Logo";
import {
  MAX_PAGES,
  PAGE_CATEGORIES,
  DEFAULT_CATEGORY,
} from "@provenleap/types/report-rendering";
import type { ReportPage } from "@provenleap/types/report-rendering";
import { PageCategoryGroup } from "./PageCategoryGroup";
import { CATEGORY_ICON_MAP } from "./categoryIconMap";

/**
 * Integration-to-category suggestion mapping per AC#4.
 * Keys are platform slugs matching the integration picker.
 */
export const INTEGRATION_CATEGORY_MAP: Record<string, string[]> = {
  "google-analytics-4": ["summary", "audience", "acquisition", "engagement"],
  "google-ads": ["acquisition", "conversions"],
  "google-search-console": ["seo"],
  "google-business-profile": ["seo"],
  "google-pagespeed-insights": ["seo"],
  "youtube-analytics": ["social", "engagement"],
  "meta-ads": ["acquisition", "conversions"],
  "facebook-pages": ["social"],
  "instagram-business": ["social"],
  "linkedin-pages": ["social"],
  "linkedin-ads": ["acquisition", "conversions"],
  "tiktok-business": ["social"],
  shopify: ["revenue", "conversions"],
  mailchimp: ["email"],
  klaviyo: ["email"],
  ahrefs: ["seo"],
  semrush: ["seo"],
  moz: ["seo"],
  callrail: ["calls"],
  calltrackingmetrics: ["calls"],
  hubspot: ["conversions", "revenue"],
};

export interface PageSelectorSidebarProps {
  pages: ReportPage[];
  activePageId: string;
  onSelectPage: (id: string) => void;
  onAddPage: (category?: string) => void;
  onDeletePage: (id: string) => void;
  onDuplicatePage: (id: string) => void;
  onReorderPages: (fromIndex: number, toIndex: number) => void;
  onRenamePage: (id: string, title: string) => boolean;
  onChangePageCategory: (id: string, category: string) => void;
  connectedIntegrations?: string[];
  reportName?: string;
  onReportNameChange?: (name: string) => void;
  /** Externally-controlled collapse state. When provided, the sidebar renders just the
   *  pages list (chromeless) and the host owns the brand header + collapse toggle. */
  collapsed?: boolean;
  /** When true, suppress the sidebar's own brand header, page-count header, and collapse
   *  footer — the host shell (route) provides those. Renders only the pages list + add-page. */
  chromeless?: boolean;
}

export function PageSelectorSidebar({
  pages,
  activePageId,
  onSelectPage,
  onAddPage,
  onDeletePage,
  onDuplicatePage,
  onReorderPages,
  onRenamePage,
  onChangePageCategory,
  connectedIntegrations = [],
  reportName: _reportName,
  onReportNameChange: _onReportNameChange,
  collapsed: collapsedProp,
  chromeless = false,
}: PageSelectorSidebarProps) {
  // DndContext sensors (separate from widget DnD)
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const keyboardSensor = useSensor(KeyboardSensor);
  const sensors = useSensors(pointerSensor, keyboardSensor);

  const isAtMaxPages = pages.length >= MAX_PAGES;
  const isSinglePage = pages.length === 1;

  // Category expand/collapse state (default all expanded)
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(
    () => Object.fromEntries(PAGE_CATEGORIES.map((c) => [c.id, true]))
  );

  // Category picker dropdown for Add New Page
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);

  // Sidebar collapse state — mirrors app Sidebar.tsx (220 / 75).
  // When the host controls it via `collapsed` prop, defer to that.
  const [collapsedInternal, setCollapsed] = useState(false);
  const collapsed = collapsedProp ?? collapsedInternal;

  // Recommended categories based on connected integrations
  const recommendedCategories = useMemo(() => {
    const recommended = new Set<string>();
    for (const integration of connectedIntegrations) {
      const categories = INTEGRATION_CATEGORY_MAP[integration];
      if (categories) {
        for (const cat of categories) {
          recommended.add(cat);
        }
      }
    }
    return recommended;
  }, [connectedIntegrations]);

  // Group pages by category
  const sortedPages = useMemo(
    () => [...pages].sort((a, b) => a.order - b.order),
    [pages]
  );

  const pagesByCategory = useMemo(() => {
    const groups: Record<string, ReportPage[]> = {};
    for (const page of sortedPages) {
      const cat = page.category ?? DEFAULT_CATEGORY;
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(page);
    }
    return groups;
  }, [sortedPages]);

  // Visible categories: only those with at least one page, in predefined order
  const visibleCategories = useMemo(
    () => PAGE_CATEGORIES.filter((c) => (pagesByCategory[c.id]?.length ?? 0) > 0),
    [pagesByCategory]
  );

  // All page IDs for SortableContext
  const allPageIds = useMemo(() => sortedPages.map((p) => p.id), [sortedPages]);

  const toggleCategory = useCallback((categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  }, []);

  // Ensure the active page's category is expanded
  const activePage = useMemo(
    () => pages.find((p) => p.id === activePageId),
    [pages, activePageId]
  );
  const activeCategory = activePage?.category ?? DEFAULT_CATEGORY;

  // Auto-expand the active page's category
  useEffect(() => {
    setExpandedCategories((prev) => {
      if (prev[activeCategory]) return prev;
      return { ...prev, [activeCategory]: true };
    });
  }, [activeCategory]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Check if dropping on a category header
    if (typeof overId === "string" && overId.startsWith("category-header-")) {
      const targetCategory = overId.replace("category-header-", "");
      onChangePageCategory(activeId, targetCategory);
      return;
    }

    // Dropping on another page — may involve cross-category move
    const draggedPage = pages.find((p) => p.id === activeId);
    const targetPage = pages.find((p) => p.id === overId);
    if (!draggedPage || !targetPage) return;

    const draggedCategory = draggedPage.category ?? DEFAULT_CATEGORY;
    const targetCategory = targetPage.category ?? DEFAULT_CATEGORY;

    if (draggedCategory !== targetCategory) {
      // Cross-category: only change category (single undo entry)
      onChangePageCategory(activeId, targetCategory);
      return;
    }

    // Same-category reorder
    const fromIndex = sortedPages.findIndex((p) => p.id === activeId);
    const toIndex = sortedPages.findIndex((p) => p.id === overId);
    if (fromIndex !== -1 && toIndex !== -1) {
      onReorderPages(fromIndex, toIndex);
    }
  };

  const handleAddPageWithCategory = (categoryId: string) => {
    onAddPage(categoryId);
    setShowCategoryPicker(false);
    // Ensure the target category is expanded
    setExpandedCategories((prev) => ({ ...prev, [categoryId]: true }));
  };

  const RootTag: typeof motion.div | "div" = chromeless ? "div" : motion.div;
  const rootProps = chromeless
    ? { className: "flex flex-col h-full overflow-hidden bg-background" }
    : {
        className: "flex flex-col h-full overflow-hidden bg-background",
        animate: { width: collapsed ? 75 : 220 },
        initial: false as const,
        transition: { type: "spring" as const, stiffness: 300, damping: 30 },
      };

  return (
    <RootTag data-testid="page-selector-sidebar" {...rootProps}>
      {/* Brand header — Favicon (collapsed) / Logo (expanded). Suppressed in chromeless mode (host owns it). */}
      {!chromeless && (
        <div className="shrink-0 px-4 pt-5 pb-1">
          <div className="relative h-12">
            <motion.div
              initial={false}
              animate={{ opacity: collapsed ? 1 : 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="absolute inset-y-0 left-[8px] flex items-center"
              aria-hidden={!collapsed}
            >
              <Favicon size={28} />
            </motion.div>
            <motion.div
              initial={false}
              animate={{ opacity: collapsed ? 0 : 1 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="absolute inset-y-0 left-[-3px] flex items-center"
              aria-hidden={collapsed}
            >
              <Logo height={58} />
            </motion.div>
          </div>
        </div>
      )}

      {/* Page-count header — kept mounted (visually hidden when chromeless) for accessibility + test ids */}
      <div
        className="flex items-center gap-2 px-4 pt-3 pb-2"
        data-testid="page-count-header"
        style={{ opacity: collapsed || chromeless ? 0 : 1, height: chromeless ? 0 : undefined, overflow: chromeless ? "hidden" : undefined, padding: chromeless ? 0 : undefined, transition: "opacity 0.2s ease" }}
      >
        <span className="text-[11px] font-semibold tracking-wider text-foreground/70 uppercase">
          Report Pages
        </span>
        <span className="text-[11px] text-muted-foreground">({pages.length})</span>
      </div>

      {/* Page list with DnD + category groups */}
      <div className={`flex-1 overflow-y-auto pb-3 ${collapsed ? "px-2" : "px-3"}`}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={allPageIds}
            strategy={verticalListSortingStrategy}
          >
            <div
              role="listbox"
              aria-label="Report pages"
              data-testid="page-list"
              className="flex flex-col gap-1"
            >
              {visibleCategories.map((category, idx) => (
                <div key={category.id}>
                  <PageCategoryGroup
                    category={category}
                    pages={pagesByCategory[category.id] ?? []}
                    isExpanded={expandedCategories[category.id] ?? true}
                    onToggle={() => toggleCategory(category.id)}
                    activePageId={activePageId}
                    onSelectPage={onSelectPage}
                    onDeletePage={onDeletePage}
                    onDuplicatePage={onDuplicatePage}
                    onRenamePage={onRenamePage}
                    onChangeCategory={onChangePageCategory}
                    totalPages={pages.length}
                    collapsed={collapsed}
                  />
                  {/* Section divider between categories (matches mockup — always rendered) */}
                  {idx < visibleCategories.length - 1 && (
                    <div
                      className="mx-2 mb-4"
                      style={{ borderBottom: "0.5px solid hsl(var(--input) / 0.5)" }}
                    />
                  )}
                </div>
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {/* Empty state helper text (hidden when collapsed) */}
        {isSinglePage && !collapsed && (
          <p
            className="mt-3 px-2 text-[12px] text-muted-foreground/50 text-center"
            data-testid="single-page-helper"
          >
            Add pages to organize your report
          </p>
        )}
      </div>

      {/* Footer — Add New Page (expanded only) + collapse toggle */}
      <div
        className="px-3 py-3 shrink-0 space-y-2"
        style={{ borderTop: "0.5px solid hsl(214 25% 70% / 0.3)" }}
      >
        {!collapsed && (
          <DropdownMenu open={showCategoryPicker} onOpenChange={setShowCategoryPicker}>
            <DropdownMenuTrigger asChild>
              <Button
                disabled={isAtMaxPages}
                variant="outline"
                className="w-full inline-flex items-center justify-center gap-1.5 shrink-0 rounded-2xl bg-background text-[13px] font-medium text-foreground shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
                style={{ border: "0.5px solid var(--hairline)" }}
                size="sm"
                data-testid="add-page-button"
              >
                <Plus size={14} />
                Add New Page
                <CaretDown size={10} className="ml-1 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="rounded-xl p-2 shadow-md w-52"
              style={{ border: "0.5px solid var(--hairline)" }}
            >
              {PAGE_CATEGORIES.map((cat) => {
                const CatIcon = CATEGORY_ICON_MAP[cat.iconName] ?? SquaresFour;
                const isRecommended = recommendedCategories.has(cat.id);
                return (
                  <DropdownMenuItem
                    key={cat.id}
                    onClick={() => handleAddPageWithCategory(cat.id)}
                    className="min-h-9 text-[13px] cursor-pointer rounded-lg"
                    data-testid={`add-page-category-${cat.id}`}
                  >
                    <CatIcon size={14} className="mr-1.5" />
                    {cat.label}
                    {isRecommended && (
                      <span className="ml-auto text-[11px] text-muted-foreground">(Recommended)</span>
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Collapse toggle — suppressed in chromeless mode (host owns it) */}
        {!chromeless && (
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="flex items-center w-full rounded-lg text-foreground bg-accent/50 hover:bg-accent/50 shadow-sm transition-colors cursor-pointer"
            style={{ border: "0.25px solid var(--hairline)" }}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            data-testid="page-sidebar-collapse"
          >
            <motion.div
              className="flex items-center py-3 cursor-pointer"
              animate={{ paddingLeft: collapsed ? 11 : 11, paddingRight: collapsed ? 0 : 12 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              {collapsed ? (
                <ArrowLineRight size={18} className="shrink-0 text-foreground" />
              ) : (
                <ArrowLineLeft size={18} className="shrink-0 text-foreground" />
              )}
              <motion.span
                initial={false}
                animate={{ opacity: collapsed ? 0 : 1, marginLeft: collapsed ? 0 : 7 }}
                transition={{
                  duration: collapsed ? 0.15 : 0.3,
                  delay: collapsed ? 0 : 0.15,
                  ease: "easeInOut",
                }}
                className="whitespace-nowrap text-sm font-medium"
              >
                Collapse
              </motion.span>
            </motion.div>
          </button>
        )}
      </div>
    </RootTag>
  );
}
